import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Link2, Unlink } from "lucide-react";
import { fmtDate } from "@/lib/date";

const words = (s) => new Set((s || "").toLowerCase().split(/[^a-z]+/).filter((w) => w.length > 2));
const score = (pbo, u) => {
  const a = words(`${pbo.name} ${pbo.partner_name || ""}`);
  return [...words(u.name)].filter((w) => a.has(w)).length;
};

function UnlinkedRow({ pbo, users, onLink }) {
  const [uid, setUid] = useState("");
  const sorted = [...users].sort((x, y) => score(pbo, y) - score(pbo, x) || x.name.localeCompare(y.name));
  return (
    <div className="flex items-center gap-2 flex-wrap py-2.5" data-testid={`pbo-link-row-${pbo.id}`}>
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium text-slate-900">{pbo.name}</div>
        <div className="text-[11px] text-slate-500">Launched {fmtDate(pbo.launch_date)} · {pbo.ibo_partner_name || "—"}{pbo.upline_team ? ` · ${pbo.upline_team}` : ""}</div>
      </div>
      <Select value={uid} onValueChange={setUid}>
        <SelectTrigger className="h-9 w-56" data-testid={`pbo-link-select-${pbo.id}`}><SelectValue placeholder="Choose partner account" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {sorted.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}{score(pbo, u) ? " · similar name — check before linking" : ""}</SelectItem>)}
        </SelectContent>
      </Select>
      <Button size="sm" disabled={!uid} onClick={() => onLink(pbo.id, uid)} data-testid={`pbo-link-btn-${pbo.id}`}
              className="bg-[#0A1F44] text-white"><Link2 className="w-4 h-4 mr-1" /> Link</Button>
    </div>
  );
}

export default function PboLinkPanel() {
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/pbo-links").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  if (!data) return null;
  return (
    <Card className="p-5" data-testid="pbo-link-panel">
      <h2 className="font-serif text-lg text-[#0A1F44]">Link PBO to partner</h2>
      <p className="text-xs text-slate-500 mb-3">PBOs are never linked automatically. When a launched PBO registers as a partner, confirm the right account here so their onboarding history carries into the partner profile. Similar names are only a hint — check before linking.</p>
      <div className="divide-y divide-slate-100">
        {data.unlinked_pbos.map((p) => (
          <UnlinkedRow key={p.id} pbo={p} users={data.unlinked_users}
                       onLink={(pid, uid) => run(() => api.post("/admin/pbo-links", { pbo_id: pid, user_id: uid }), "Linked")} />
        ))}
        {!data.unlinked_pbos.length && <div className="text-sm text-slate-500 py-2">Every launched PBO is linked.</div>}
      </div>
      {data.linked.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold mb-1">Linked</div>
          {data.linked.map((p) => (
            <div key={p.id} className="flex items-center justify-between gap-2 py-1.5 text-sm" data-testid={`pbo-linked-${p.id}`}>
              <span>{p.name} → <b>{p.linked_user?.name}</b> <span className="text-[11px] text-slate-500">({p.link_history?.at(-1)?.method === "auto_email" ? "earlier auto email match" : "admin"})</span></span>
              <Button size="sm" variant="ghost" className="text-[#8A1F1F]" data-testid={`pbo-unlink-${p.id}`}
                      onClick={() => run(() => api.delete(`/admin/pbo-links/${p.id}`), "Unlinked")}><Unlink className="w-4 h-4" /></Button>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
